<template>
	<div class="main personal-center" :style="themeStyle">
		<div class="box">
			<Header />
			<GameBanner :src="gameData.topBgImage" :variant="gameData.extraFields.bannerType"
				:drawProductId="gameData.extraFields.bannerDrawProductId" />
			<div class="personal-center-body">
				<div class="content">
					<UserInfo ref="userInfo" :show-summary="false" />

					<section class="position">
						<aside class="side-panel">
							<div class="account-nav-header">
								<i class="el-icon-box account-nav-icon" aria-hidden="true"></i>
								<h1 class="account-nav-title">{{ $t('personalCenter.title') }}</h1>
							</div>
							<div class="ledger-menu">
								<button v-for="item in categories" :key="item.value" type="button"
									class="ledger-card" :class="{ active: category === item.value }"
									:aria-current="category === item.value ? 'page' : null"
									@click="selectCategory(item.value)">
									<i :class="item.icon" class="ledger-icon" aria-hidden="true"></i>
									<span class="ledger-title">{{ item.label }}</span>
								</button>
							</div>
						</aside>

						<section class="main-panel">
							<section v-if="category === 'exchange'" class="quick-panel">
								<div>
									<div class="quick-title">{{ $t('personalCenter.redeem_gift') }}</div>
									<div class="quick-sub">{{ $t('personalCenter.redeem_description') }}</div>
								</div>
								<div class="redeem-row">
									<input v-model.trim="redeemCode" class="redeem-input"
										:aria-label="$t('personalCenter.redeem_code')"
										:placeholder="$t('personalCenter.redeem_code_placeholder')" :disabled="isRedeeming"
										@keyup.enter="redeem">
									<button class="primary-button" type="button" :disabled="isRedeeming" @click="redeem">
										{{ isRedeeming ? $t('personalCenter.redeeming') : $t('personalCenter.redeem_now') }}
									</button>
								</div>
							</section>

							<section v-if="category !== 'vip'" class="record-panel">
								<div class="record-list">
									<div v-if="pageRecords.length" class="record-table">
										<div class="table-row-head" :class="category + '-grid'">
											<span v-for="heading in headings" :key="heading">{{ heading }}</span>
										</div>
										<div v-for="record in pageRecords" :key="record.id" class="table-row-item"
											:class="category + '-grid'">
											<template v-if="category === 'exchange'">
												<span :data-label="$t('personalCenter.exchange_time')">{{ record.time }}</span>
												<span :data-label="$t('personalCenter.redeem_code')">{{ record.title }}</span>
												<span :data-label="$t('personalCenter.reward_content')">{{ record.desc }}</span>
												<span :data-label="$t('personalCenter.status')"><i class="status" :class="record.badge">{{ $t('personalCenter.exchange_success') }}</i></span>
											</template>
											<template v-else-if="category === 'points'">
												<span :data-label="$t('personalCenter.time')">{{ record.time }}</span>
												<span :data-label="$t('personalCenter.type')">{{ record.status }}</span>
												<span :data-label="$t('personalCenter.points_change')" :class="record.valueClass">{{ record.value }}</span>
												<span :data-label="$t('personalCenter.balance')">{{ record.balance }}</span>
											</template>
											<template v-else-if="category === 'lottery'">
												<span :data-label="$t('personalCenter.lottery_time')">{{ record.time }}</span>
												<span :data-label="$t('personalCenter.reward')">{{ record.title }}</span>
											</template>
											<template v-else>
												<span class="order-cell" :data-label="$t('personalCenter.order_id')">
													<b>{{ record.title }}</b>
													<button class="copy-button" type="button" @click="copyText(record.title)">{{ $t('personalCenter.copy') }}</button>
												</span>
												<span :data-label="$t('personalCenter.payment_channel')">{{ record.desc }}</span>
												<span :data-label="$t('personalCenter.amount')">{{ record.value }}</span>
												<span :data-label="$t('personalCenter.server')">{{ record.meta[1] }}</span>
												<span :data-label="$t('personalCenter.role')">{{ record.meta[2] }}</span>
												<span :data-label="$t('personalCenter.payment_time')">{{ record.meta[3] || record.time }}</span>
											</template>
										</div>
									</div>
									<div v-else class="empty-state">
										<strong>{{ isCurrentCategoryLoading ? $t('personalCenter.loading') : $t('personalCenter.no_records') }}</strong>
										<p v-if="!isCurrentCategoryLoading">{{ $t('personalCenter.no_content') }}</p>
									</div>
								</div>
								<div v-if="pageRecords.length || totalPages > 1" class="pagination">
									<button class="page-button" type="button" :disabled="page === 1 || isCurrentCategoryLoading" @click="prevPage">{{ $t('personalCenter.previous') }}</button>
									<span class="page-info">{{ $t('personalCenter.page_info', { current: page, total: totalPages }) }}</span>
									<button class="page-button" type="button" :disabled="page === totalPages || isCurrentCategoryLoading" @click="nextPage">{{ $t('personalCenter.next') }}</button>
								</div>
							</section>

							<VipMemberOverview v-else-if="category === 'vip'" :game-data="gameData" />
						</section>
					</section>
				</div>
			</div>
			<Footer />
		</div>
	</div>
</template>

<script>
import Header from '../components/header.vue'
import Footer from '../components/footer.vue'
import UserInfo from '../components/userInfo.vue'
import GameBanner from '../components/GameBanner.vue'
import VipMemberOverview from '../components/common/VipMemberOverview.vue'

export default {
	name: 'personalCenter',
	components: { Header, Footer, UserInfo, GameBanner, VipMemberOverview },
	data() {
		const hasVip = this.isVipEnabled();
		const availableCategories = hasVip
			? ['vip', 'exchange', 'points', 'lottery', 'recharge']
			: ['exchange', 'points', 'lottery', 'recharge']
		const queryCategory = this.$route && this.$route.query ? this.$route.query.tab : ''
		return {
			gameData: this.$store.state.gameData,
			availableCategories,
			category: availableCategories.includes(queryCategory) ? queryCategory : availableCategories[0],
			page: 1,
			pageSize: 6,
			redeemCode: '',
			isRedeeming: false,
			isLoadingExchange: false,
			isLoadingPoints: false,
			isLoadingRecharge: false,
			isLoadingLottery: false,
			rechargeTotal: 0,
			records: []
		}
	},
	mounted() {
		this.loadGiftRecords()
		this.loadPointsRecords()
		this.loadLotteryRecords()
	},
	watch: {
		'$route.query.tab'(tab) {
			const category = this.availableCategories.includes(tab) ? tab : this.availableCategories[0]
			if (category !== this.category) this.selectCategory(category)
		}
	},
	computed: {
		categories() {
			return [
				{ value: 'vip', label: this.$t('personalCenter.vip_member'), icon: 'el-icon-medal' },
				{ value: 'exchange', label: this.$t('personalCenter.my_exchange'), icon: 'el-icon-present' },
				{ value: 'points', label: this.$t('personalCenter.my_points'), icon: 'el-icon-coin' },
				{ value: 'lottery', label: this.$t('personalCenter.lottery_records'), icon: 'el-icon-trophy' },
				{ value: 'recharge', label: this.$t('personalCenter.recharge_records'), icon: 'el-icon-tickets' }
			].filter(item => this.availableCategories.includes(item.value))
		},
		themeStyle() {
			const configuredColor = this.gameData && this.gameData.theme
			const color = /^#[0-9a-f]{6}$/i.test(configuredColor || '') ? configuredColor : '#D26A2C'
			const rgb = color.match(/[a-f\d]{2}/gi).map(value => parseInt(value, 16)).join(', ')
			return { '--theme-color': color, '--theme-color-rgb': rgb }
		},
		filteredRecords() {
			return this.records.filter(record => record.type === this.category)
		},
		totalPages() {
			if (this.category === 'recharge') {
				return Math.max(1, Math.ceil(this.rechargeTotal / this.pageSize))
			}
			return Math.max(1, Math.ceil(this.filteredRecords.length / this.pageSize))
		},
		pageRecords() {
			if (this.category === 'recharge') return this.filteredRecords
			const start = (this.page - 1) * this.pageSize
			return this.filteredRecords.slice(start, start + this.pageSize)
		},
		isCurrentCategoryLoading() {
			return (this.category === 'exchange' && this.isLoadingExchange) ||
				(this.category === 'points' && this.isLoadingPoints) ||
				(this.category === 'recharge' && this.isLoadingRecharge) ||
				(this.category === 'lottery' && this.isLoadingLottery)
		},
		headings() {
			if (this.category === 'vip') return []
			const headings = {
				exchange: ['exchange_time', 'redeem_code', 'reward_content', 'status'],
				points: ['time', 'type', 'points_change', 'balance'],
				lottery: ['lottery_time', 'reward'],
				recharge: ['order_id', 'payment_channel', 'amount', 'server', 'role', 'payment_time']
			}
			return headings[this.category].map(key => this.$t(`personalCenter.${key}`))
		}
	},
	methods: {
		isVipEnabled:function() {
			const extraFields = this.$store.state.gameData.extraFields
			return !!extraFields && extraFields.enableVip === true;
		},
		async loadLotteryRecords(showLogin = false) {
			if (!this.$session.hasLogin() || !this.$session.getUserId()) {
				if (showLogin) this.$refs.userInfo.openLoginWindow()
				return
			}

			this.isLoadingLottery = true
			try {
				const response = await this.$axios({
					method: 'get',
					url: '/web/api/scoreActivity/lottery-record',
					silentError: true,
					params: {
						userId: this.$session.getUserId(),
						gameCode: this.$cache.getGameCode(),
						gameLanguage: (this.gameData && this.gameData.language) ||
							(this.$i18n && this.$i18n.locale) || 'en_US'
					}
				})

				if (response.code === 1000) {
					const data = response.data || []
					const lotteryList = Array.isArray(data) ? data : (data.records || data.list || [])
					const lotteryRecords = lotteryList.map((item, index) => ({
						id: item.id || `lottery-${item.date || index}-${index}`,
						type: 'lottery',
						time: item.date || '',
						title: item.name || ''
					}))
					this.records = this.records.filter(record => record.type !== 'lottery').concat(lotteryRecords)
					if (this.page > this.totalPages) this.page = this.totalPages
				}
			} catch (error) {
				console.error('Failed to request lottery records:', error)
			} finally {
				this.isLoadingLottery = false
			}
		},
		prevPage() {
			if (this.page <= 1) return
			this.page--
			if (this.category === 'recharge') this.loadOrderRecords()
		},
		nextPage() {
			if (this.page >= this.totalPages) return
			this.page++
			if (this.category === 'recharge') this.loadOrderRecords()
		},
		async loadOrderRecords() {
			if (!this.$session.hasLogin() || !this.$session.getUserId()) {
				this.$refs.userInfo.openLoginWindow()
				return
			}

			this.isLoadingRecharge = true
			this.records = this.records.filter(record => record.type !== 'recharge')
			try {
				const response = await this.$axios({
					method: 'get',
					url: '/web/api/payment/orderRecords',
					silentError: true,
					params: {
						userId: this.$session.getUserId(),
						gameCode: this.$cache.getGameCode(),
						pageNo: this.page,
						pageSize: this.pageSize
					}
				})

				if (response.code === 1000) {
					const data = response.data || {}
					const orderList = Array.isArray(data.orderList) ? data.orderList : []
					const orderRecords = orderList.map((item, index) => ({
						id: item.orderId || `order-${this.page}-${index}`,
						type: 'recharge',
						time: item.time || '',
						title: item.orderId || '',
						desc: this.capitalizeFirstLetter(item.cashflow),
						value: [item.currency, item.amount].filter(value => value !== undefined && value !== null && value !== '').join(' '),
						meta: ['', item.serverName || '', item.roleName || '', item.time || '']
					}))
					this.rechargeTotal = Number(data.totalNum) || 0
					this.records = this.records.concat(orderRecords)
				}
			} catch (error) {
				console.error('Failed to request top-up records:', error)
			} finally {
				this.isLoadingRecharge = false
			}
		},
		capitalizeFirstLetter(value) {
			if (!value) return ''
			return value.charAt(0).toUpperCase() + value.slice(1)
		},
		formatDateTime(value) {
			if (!value) return ''
			const date = new Date(value)
			if (Number.isNaN(date.getTime())) return value
			const pad = number => String(number).padStart(2, '0')
			return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
				`${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
		},
		async loadPointsRecords(showRoleChooser = false) {
			if (!this.$session.hasLogin() || !this.$session.getUserId()) return

			const role = this.$cache.getRoleInfo() || {}
			if (!role.roleId || !role.serverCode) {
				if (showRoleChooser) this.$refs.userInfo.openRoleChooseWindow()
				return
			}

			this.isLoadingPoints = true
			try {
				const response = await this.$axios({
					method: 'get',
					url: '/web/api/points/user/rewards',
					silentError: true,
					params: {
						userId: this.$session.getUserId(),
						gameCode: this.$cache.getGameCode(),
						roleId: role.roleId,
						serverCode: role.serverCode
					}
				})

				if (response.code === 1000) {
					const data = response.data || []
					const pointsList = Array.isArray(data)
						? data
						: (data.list || data.records || data.rewardList || [])
					const sourceLabels = {
						1: this.$t('personalCenter.source_recharge_reward'),
						2: this.$t('personalCenter.source_official_activity'),
						3: this.$t('personalCenter.source_item_exchange')
					}
					const pointsRecords = pointsList.map((item, index) => {
						const points = Number(item.points)
						return {
							id: `points-${item.createTime || index}-${index}`,
							type: 'points',
							status: sourceLabels[item.pointsSource] || item.pointsSource || '',
							time: this.formatDateTime(item.createTime),
							value: Number.isNaN(points) ? item.points : (points > 0 ? `+${points}` : String(points)),
							valueClass: points >= 0 ? 'plus' : 'minus',
							balance: item.balance
						}
					})
					this.records = this.records.filter(record => record.type !== 'points').concat(pointsRecords)
					if (this.page > this.totalPages) this.page = this.totalPages
				}
			} catch (error) {
				console.error('Failed to request points records:', error)
			} finally {
				this.isLoadingPoints = false
			}
		},
		async loadGiftRecords() {
			if (!this.$session.hasLogin() || !this.$session.getUserId()) return

			this.isLoadingExchange = true
			try {
				const response = await this.$axios({
					method: 'get',
					url: '/web/api/act/rebateCoupon/gift/list',
					silentError: true,
					params: {
						gameCode: this.$cache.getGameCode(),
						userId: this.$session.getUserId()
					}
				})

				if (response.code === 1000) {
					const data = response.data || []
					const giftList = Array.isArray(data)
						? data
						: (data.list || data.records || data.giftList || [])
					const giftRecords = giftList.map((item, index) => ({
						id: `${item.giftCode || 'gift'}-${item.receivedTime || index}`,
						type: 'exchange',
						time: this.formatDateTime(item.receivedTime),
						title: item.giftCode || '',
						desc: item.giftType === undefined || item.giftType === null || item.giftType === ''
							? ''
							: this.$t('personalCenter.rebate_coupon', { percent: item.giftType }),
						badge: 'status-success'
					}))
					this.records = this.records.filter(record => record.type !== 'exchange').concat(giftRecords)
					if (this.page > this.totalPages) this.page = this.totalPages
				}
			} catch (error) {
				console.error('Failed to request redemption records:', error)
			} finally {
				this.isLoadingExchange = false
			}
		},
		selectCategory(category) {
			if (!this.availableCategories.includes(category)) return
			this.category = category
			this.page = 1
			if (category === 'exchange') this.loadGiftRecords()
			if (category === 'points') this.loadPointsRecords(true)
			if (category === 'recharge') this.loadOrderRecords()
			if (category === 'lottery') this.loadLotteryRecords(true)
		},
		async redeem() {
			if (this.isRedeeming) return
			if (!this.redeemCode) {
				this.$message.warning(this.$t('personalCenter.enter_redeem_code'))
				return
			}
			if (!this.$session.hasLogin() || !this.$session.getUserId() || !this.$session.getLoginToken()) {
				this.$refs.userInfo.openLoginWindow()
				return
			}

			const giftCode = this.redeemCode
			this.isRedeeming = true
			try {
				const response = await this.$axios({
					method: 'get',
					url: '/web/api/act/rebateCoupon/gift',
					silentError: true,
					headers: {
						'X-Auth-Token': this.$session.getLoginToken()
					},
					params: {
						userId: this.$session.getUserId(),
						gameCode: this.$cache.getGameCode(),
						giftCode
					}
				})

				if (response.code === 1000) {
					this.redeemCode = ''
					this.$message.success(response.message || this.$t('personalCenter.exchange_success'))
					await this.loadGiftRecords()
				}
			} catch (error) {
				console.error('Failed to redeem gift code:', error)
			} finally {
				this.isRedeeming = false
			}
		},
		copyText(text) {
			navigator.clipboard.writeText(text)
				.then(() => this.$message.success(this.$t('personalCenter.copy_success')))
				.catch(() => this.$message.error(this.$t('personalCenter.copy_failed')))
		}
	}
}
</script>

<style scoped>
.personal-center {
	--theme-color: #d26a2c;
	--theme-color-rgb: 210, 106, 44;
	--pc-text: #20242b;
	--pc-text-secondary: #69707d;
	--pc-line: #e7e9ed;
	--pc-soft: #f7f8fa;
	--pc-surface: #fff;
	--text-muted: #7d8490;
	min-height: 100vh;
	background: #f3f5f7;
}

.personal-center > .box > .game-banner {
	margin-bottom: 0;
}

.personal-center-body {
	min-height: 720px;
	padding-bottom: 64px;
	background: #f3f5f7;
}

.personal-center-body .content {
	width: 15rem;
	max-width: calc(100% - 40px);
	min-height: 0;
	margin-right: auto;
	margin-left: auto;
	margin-top: 0;
	padding-top: 24px;
	position: relative;
	z-index: 2;
}

.position {
	display: grid;
	grid-template-columns: 3.2rem minmax(0, 1fr);
	gap: 18px;
	box-sizing: border-box;
	width: 100%;
	min-height: 640px;
	margin-top: 0;
	padding: 0;
	background: transparent;
	box-shadow:none;
	font-size: 16px;
}

.side-panel {
	min-width: 0;
	align-self: stretch;
	overflow: hidden;
	border: 1px solid var(--pc-line);
	border-radius: 8px;
	background: #fff;
	box-shadow: 0 8px 24px rgba(28, 35, 43, .08);
}

.account-nav-header {
	display: flex;
	min-height: 76px;
	align-items: center;
	gap: 12px;
	padding: 0 20px;
	border-bottom: 1px solid var(--pc-line);
	background: #fff;
}

.account-nav-icon {
	flex: 0 0 auto;
	color: var(--theme-color);
	font-size: 24px;
}

.account-nav-title {
	margin: 0;
	color: var(--theme-color);
	font-size: 17px;
	font-weight: 800;
	line-height: 24px;
}

.ledger-menu {
	display: grid;
	gap: 4px;
	padding: 12px;
}

.ledger-card {
	position: relative;
	display: flex;
	min-height: 54px;
	align-items: center;
	gap: 12px;
	box-sizing: border-box;
	width: 100%;
	border: 0;
	border-radius: 6px;
	padding: 0 14px;
	color: #424852;
	background: transparent;
	font-size: 14px;
	font-weight: 600;
	text-align: left;
	cursor: pointer;
	transition: color .16s ease, background-color .16s ease;
}

.ledger-card:hover:not(.active) {
	color: var(--pc-text);
	background: #f5f7f9;
}

.ledger-card.active {
	color: #272d35;
	background: #f6f8fa;
	font-weight: 700;
}

.ledger-card.active::before {
	position: absolute;
	top: 11px;
	bottom: 11px;
	left: 0;
	width: 3px;
	border-radius: 0 3px 3px 0;
	background: var(--theme-color);
	content: '';
}

.ledger-icon {
	display: grid;
	width: 32px;
	height: 32px;
	flex: 0 0 auto;
	place-items: center;
	color: #747b84;
	font-size: 19px;
	line-height: 1;
	text-align: center;
}

.ledger-card.active .ledger-icon {
	border-radius: 6px;
	color: var(--theme-color);
	background: rgba(var(--theme-color-rgb), .1);
}

.ledger-title {
	min-width: 0;
	overflow-wrap: anywhere;
}

.main-panel {
	display: grid;
	min-width: 0;
	align-content: start;
	gap: 18px;
	padding: 0;
}

.quick-panel {
	display: grid;
	grid-template-columns: minmax(180px, .65fr) minmax(320px, 1.35fr);
	align-items: center;
	gap: 24px;
	border: 1px solid rgba(var(--theme-color-rgb), .2);
	border-radius: 8px;
	padding: 20px;
	background: #fff;
}

.quick-title {
	color: var(--pc-text);
	font-size: 16px;
	font-weight: 800;
	line-height: 22px;
}

.quick-sub {
	margin-top: 5px;
	color: var(--pc-text-secondary);
	font-size: 13px;
	line-height: 20px;
}

.redeem-row {
	display: grid;
	grid-template-columns: minmax(180px, 1fr) auto;
	gap: 10px;
	width: 100%;
	min-width: 0;
}

.redeem-input {
	box-sizing: border-box;
	height: 44px;
	min-width: 0;
	border: 1px solid #d9dde3;
	border-radius: 6px;
	padding: 0 14px;
	outline: none;
	color: var(--pc-text);
	background: #fff;
	font-size: 14px;
	transition: border-color .16s ease, box-shadow .16s ease;
}

.redeem-input::placeholder { color: #9aa0aa; }

.redeem-input:focus {
	border-color: var(--theme-color);
	box-shadow: 0 0 0 3px rgba(var(--theme-color-rgb), .11);
}

.primary-button,
.page-button {
	min-height: 40px;
	border: 1px solid var(--theme-color);
	border-radius: 6px;
	padding: 0 16px;
	color: #fff;
	background: var(--theme-color);
	font-size: 14px;
	font-weight: 700;
	white-space: nowrap;
	cursor: pointer;
	transition: opacity .16s ease, box-shadow .16s ease;
}

.primary-button { min-height: 44px; }
.primary-button:hover:not(:disabled), .page-button:hover:not(:disabled) { box-shadow: 0 5px 12px rgba(var(--theme-color-rgb), .2); }
.primary-button:focus-visible, .page-button:focus-visible, .ledger-card:focus-visible, .copy-button:focus-visible { outline: 2px solid rgba(var(--theme-color-rgb), .45); outline-offset: 2px; }
.primary-button:disabled { cursor: wait; opacity: .62; }

.record-panel {
	overflow: hidden;
	border: 1px solid var(--pc-line);
	border-radius: 8px;
	background: var(--pc-surface);
}

.record-list { background: var(--pc-surface); }
.record-table { overflow: hidden; background: var(--pc-surface); }

.table-row-head,
.table-row-item {
	display: grid;
	min-height: 52px;
	align-items: center;
	gap: 14px;
	box-sizing: border-box;
	padding: 0 18px;
}

.table-row-head {
	border-bottom: 1px solid var(--pc-line);
	color: #5d6571;
	background: var(--pc-soft);
	font-size: 12px;
	font-weight: 800;
}

.table-row-item {
	border-bottom: 1px solid #eef0f2;
	color: #363c45;
	font-size: 13px;
	line-height: 19px;
	transition: background-color .16s ease;
}

.table-row-item:last-child { border-bottom: 0; }
.table-row-item:hover { background: #fbfcfd; }
.table-row-item span { min-width: 0; overflow-wrap: anywhere; }
.exchange-grid, .points-grid { grid-template-columns: 1.15fr 1fr 1.2fr .82fr; }
.lottery-grid { grid-template-columns: 2.25rem minmax(0, 1fr); }
.recharge-grid { grid-template-columns: 1.55fr .82fr .9fr 1.1fr 1fr 1.25fr; }
.order-cell { display: flex; align-items: center; gap: 8px; }
.order-cell b { color: var(--pc-text); font-weight: 700; }

.copy-button {
	border: 0;
	padding: 4px 6px;
	color: var(--theme-color);
	background: transparent;
	font-size: 12px;
	font-weight: 700;
	cursor: pointer;
}

.status {
	display: inline-flex;
	min-height: 26px;
	align-items: center;
	border-radius: 999px;
	padding: 0 10px;
	font-size: 12px;
	font-style: normal;
	font-weight: 700;
}

.status-success { color: #14794c; background: #e9f7f0; }
.plus { color: #16865a; font-weight: 800; }
.minus { color: #b64d37; font-weight: 800; }

.empty-state,
.vip-member-state {
	display: grid;
	min-height: 330px;
	place-items: center;
	align-content: center;
	box-sizing: border-box;
	padding: 32px;
	color: var(--pc-text-secondary);
	background: #fff;
	text-align: center;
}

.vip-member-state {
	border: 1px solid var(--pc-line);
	border-radius: 8px;
}

.empty-state::before,
.vip-member-state::before {
	display: block;
	width: 38px;
	height: 4px;
	margin-bottom: 18px;
	border-radius: 2px;
	background: rgba(var(--theme-color-rgb), .32);
	content: '';
}

.empty-state strong,
.vip-member-state strong {
	color: var(--pc-text);
	font-size: 16px;
	font-weight: 800;
	line-height: 24px;
}

.empty-state p,
.vip-member-state p {
	margin: 7px 0 0;
	font-size: 13px;
	line-height: 20px;
}

.pagination {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 14px;
	padding: 14px 18px;
	border-top: 1px solid var(--pc-line);
	background: #fafbfc;
}

.page-button {
	min-width: 72px;
	min-height: 34px;
	border-color: #d9dde3;
	color: #424953;
	background: #fff;
	font-size: 13px;
}

.page-button:disabled { color: #a1a6ae; background: #f0f1f3; cursor: not-allowed; }
.page-info { min-width: 72px; color: var(--pc-text-secondary); font-size: 13px; text-align: center; }

@media (max-width: 1024px) {
	.personal-center-body .content { max-width: calc(100% - 28px); }
	.position { grid-template-columns: 1fr; min-height: 0; gap: 14px; }
	.side-panel { align-self: start; }
	.account-nav-header { min-height: 60px; }
	.ledger-menu { grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); padding-top: 10px; }
	.ledger-card { justify-content: center; gap: 8px; padding: 0 8px; text-align: center; }
	.ledger-card.active::before { top: auto; right: 12px; bottom: 0; left: 12px; width: auto; height: 3px; border-radius: 2px 2px 0 0; }
	.quick-panel { grid-template-columns: 1fr; gap: 16px; }
	.redeem-row { min-width: 0; }
}

@media (max-width: 767px) {
	.personal-center > .box > .game-banner--has-mobile-winners { margin-bottom: 1.15rem; }
	.personal-center-body { min-height: 0; padding-bottom: 28px; }
	.personal-center-body .content { max-width: calc(100% - 16px); margin-top: 0; padding-top: 24px; }
	.position { width: 100%; margin-top: 0; box-sizing: border-box; border-radius: 8px; }
	.account-nav-header { display: none; }
	.side-panel { overflow: hidden; background: #fff; }
	.ledger-menu { display: flex; gap: 4px; overflow-x: auto; padding: 8px; scrollbar-width: none; }
	.ledger-menu::-webkit-scrollbar { display: none; }
	.ledger-card { width: auto; min-width: max-content; min-height: 42px; flex: 0 0 auto; padding: 0 13px; }
	.ledger-icon { width: 26px; height: 26px; font-size: 17px; }
	.main-panel { gap: 12px; padding: 0; }
	.quick-panel { gap: 14px; padding: 16px; }
	.redeem-row { grid-template-columns: 1fr; }
	.primary-button { width: 100%; }
	.record-panel { overflow: visible; border: 0; background: transparent; }
	.record-list { padding: 0; }
	.record-table { overflow: visible; border: 0; background: transparent; }
	.table-row-head { display: none; }
	.table-row-item { display: grid; grid-template-columns: 1fr; min-height: 0; gap: 9px; margin-bottom: 10px; border: 1px solid var(--pc-line); border-radius: 8px; padding: 14px; background: #fff; box-shadow: 0 3px 10px rgba(28, 35, 43, .04); }
	.table-row-item:last-child { border-bottom: 1px solid var(--pc-line); }
	.table-row-item > span { display: grid; grid-template-columns: 92px minmax(0, 1fr); gap: 10px; font-size: 13px; line-height: 19px; }
	.table-row-item > span::before { color: var(--text-muted); font-weight: 700; content: attr(data-label); }
	.order-cell { grid-template-columns: 92px minmax(0, 1fr) auto !important; }
	.copy-button { border: 1px solid rgba(var(--theme-color-rgb), .28); border-radius: 6px; padding: 4px 8px; }
	.empty-state, .vip-member-state { min-height: 260px; border: 1px solid var(--pc-line); border-radius: 8px; }
	.pagination { gap: 8px; border: 1px solid var(--pc-line); border-radius: 8px; }
	.page-button { min-width: 64px; padding: 0 10px; }
}
</style>
